/**
 * Manage Office Dialog API for OAuth authentication
 */

export interface DialogResponse {
  token?: string
  refreshToken?: string | null
  tokenType?: string
  error?: string
  success?: boolean
}

export async function openGoogleAuthDialog(): Promise<{
  token: string
  refreshToken: string | null
} | null> {
  return new Promise((resolve, reject) => {
    const Office = window.Office
    if (!Office) {
      const errorMsg = 'Office context not available'
      console.error(errorMsg)
      reject(new Error(errorMsg))
      return
    }

    console.log('Opening Google OAuth Dialog')

    try {
      const protocol = window.location.protocol
      const host = window.location.host
      // google-auth-start.html will redirect to Supabase OAuth
      const dialogUrl = `${protocol}//${host}/google-auth-start.html`

      Office.context.ui.displayDialogAsync(
        dialogUrl,
        { height: 70, width: 40 },
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            const dialog = result.value
            let resolved = false

            const handler = (
              event: Office.DialogParentMessageReceivedEventArgs
            ) => {
              if (!resolved) {
                resolved = true
                try {
                  const message = JSON.parse(event.message) as DialogResponse

                  if (message.error) {
                    console.error('Auth error from dialog:', message.error)
                    // Close dialog before rejecting to avoid "load failed" errors
                    dialog.close()
                    setTimeout(() => reject(new Error(message.error)), 100)
                  } else if (message.token) {
                    dialog.close()
                    const result = {
                      token: message.token,
                      refreshToken: message.refreshToken || null,
                    }
                    setTimeout(() => resolve(result), 100)
                  } else {
                    console.error('Invalid response from dialog')
                    dialog.close()
                    setTimeout(() => resolve(null), 100)
                  }
                } catch (error) {
                  console.error('Error parsing dialog message', error)
                  dialog.close()
                  setTimeout(
                    () =>
                      reject(
                        new Error(
                          `Failed to parse dialog response: ${error instanceof Error ? error.message : String(error)}`
                        )
                      ),
                    100
                  )
                }
              }
            }

            dialog.addEventHandler(
              Office.EventType.DialogMessageReceived,
              handler
            )

            // Handle dialog close and errors
            dialog.addEventHandler(
              Office.EventType.DialogEventReceived,
              (event) => {
                const errorOrMessage =
                  'error' in event
                    ? event.error
                    : 'message' in event
                      ? event.message
                      : undefined

                // If already resolved (message received), ignore dialog events
                // This prevents "load failed" errors after successful auth
                if (resolved) {
                  console.log(
                    'Dialog event received after resolution (ignoring):',
                    errorOrMessage
                  )
                  return
                }

                if (errorOrMessage === 12002) {
                  // Dialog closed by user
                  console.log('Dialog closed by user')
                  resolved = true
                  resolve(null)
                } else if (errorOrMessage === 12006) {
                  // Dialog navigation error
                  console.error('Dialog navigation failed (error 12006)')
                  resolved = true
                  reject(
                    new Error(
                      'Signing in with Google failed or cancelled. Please try again.'
                    )
                  )
                } else if (errorOrMessage) {
                  console.error('Dialog error code:', errorOrMessage)
                  resolved = true
                  reject(new Error(`Dialog error: ${errorOrMessage}`))
                }
              }
            )
          } else {
            const errorMsg = `Failed to open dialog: ${result.error.name} - ${result.error.message}`
            console.error(errorMsg)
            reject(new Error(errorMsg))
          }
        }
      )
    } catch (error) {
      console.error('Exception opening dialog:', error)
      reject(error)
    }
  })
}

/**
 * Close the OAuth dialog (called from callback page)
 */
export function closeAuthDialog() {
  try {
    const Office = window.Office
    if (Office?.context?.ui) {
      Office.context.ui.messageParent('')
    }
  } catch (error) {
    console.error('Error closing dialog:', error)
  }
}
